import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SidebarLayout from '../../components/SidebarLayout';
import UserSearchDropdown from '../../components/UserSearchDropdown';
import ViewDataContent from '../../components/ViewDataContent';
import SchemaAddDataPanel from '../../components/schemas/SchemaAddDataPanel';
import SchemaEditTableModal from '../../components/schemas/SchemaEditTableModal';
import './styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const AdminSchoolDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [users, setUsers] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [viewSchemaId, setViewSchemaId] = useState(null);
  const [addSchemaId, setAddSchemaId] = useState(null);
  const [editSchemaId, setEditSchemaId] = useState(null);
  const [viewIncludeDeleted, setViewIncludeDeleted] = useState(false);
  const [viewRefreshSignal, setViewRefreshSignal] = useState(null);
  const [updatingSchemaId, setUpdatingSchemaId] = useState(null);
  const viewPanelRef = useRef(null);

  useEffect(() => {
    if (id) {
      fetchSchoolDetails();
    }
  }, [id]);

  useEffect(() => {
    const handleEscClose = (event) => {
      if (event.key !== 'Escape') return;
      if (addSchemaId || editSchemaId || viewSchemaId) {
        if (addSchemaId) setAddSchemaId(null);
        if (editSchemaId) setEditSchemaId(null);
        if (viewSchemaId) setViewSchemaId(null);
      }
    };

    window.addEventListener('keydown', handleEscClose);
    return () => window.removeEventListener('keydown', handleEscClose);
  }, [addSchemaId, editSchemaId, viewSchemaId]);

  const fetchSchoolDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch school details');
      }

      const data = await response.json();
      setSchool(data.data.school);
      setUsers(data.data.users || []);
      setSchemas(data.data.schemas || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const schemaMap = useMemo(() => {
    const map = new Map();
    schemas.forEach((schema) => {
      map.set(schema.schema_id, schema);
    });
    return map;
  }, [schemas]);

  useEffect(() => {
    if (viewSchemaId && viewPanelRef.current) {
      requestAnimationFrame(() => {
        viewPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [viewSchemaId]);

  const handleViewDataClick = (schemaId) => {
    setViewIncludeDeleted(false);
    setViewSchemaId((prev) => {
      if (prev === schemaId) {
        return null;
      }
      setViewRefreshSignal(Date.now());
      return schemaId;
    });
  };

  const handleAddDataSuccess = (_, schemaIdFromChild) => {
    if (viewSchemaId && schemaIdFromChild && viewSchemaId === schemaIdFromChild) {
      setViewRefreshSignal(Date.now());
    }
  };

  const handleEditSchemaSuccess = (type, schemaIdFromChild) => {
    fetchSchoolDetails();
    if (!schemaIdFromChild) return;
    if (type === 'delete' && viewSchemaId === schemaIdFromChild) {
      setViewSchemaId(null);
    } else if (viewSchemaId === schemaIdFromChild) {
      setViewRefreshSignal(Date.now());
    }
  };

  const handleToggleSchemaActive = async (schema) => {
    const nextActive = schema.is_active === false;
    const actionLabel = nextActive ? 'activate' : 'deactivate';
    const confirmed = window.confirm(
      nextActive
        ? `Activate table "${schema.table_name}"? It will become visible to users of this entity again.`
        : `Deactivate table "${schema.table_name}"? It will be hidden from entity users until activated again.`
    );
    if (!confirmed) return;

    setUpdatingSchemaId(schema.schema_id);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API_URL}/admin/entities/${id}/schemas/${schema.schema_id}/status`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ is_active: nextActive })
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || `Failed to ${actionLabel} table`);
      }
      setSchemas((prev) =>
        prev.map((item) =>
          item.schema_id === schema.schema_id
            ? { ...item, is_active: data.data?.is_active ?? nextActive }
            : item
        )
      );
    } catch (err) {
      alert(err.message);
    } finally {
      setUpdatingSchemaId(null);
    }
  };

  const handleUserSelect = async (selectedUser) => {
    setAddingUser(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/entities/${id}/add-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: selectedUser.user_id })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add user');
      }

      const data = await response.json();
      alert(`User ${selectedUser.full_name || selectedUser.username} added successfully!`);
      setShowAddUser(false);
      fetchSchoolDetails(); // Refresh data
    } catch (err) {
      alert(err.message);
    } finally {
      setAddingUser(false);
    }
  };

  if (loading) {
    const skeletonCards = Array.from({ length: 3 });
    return (
      <SidebarLayout>
        <div className="admin-school-details-container">
          <div className="admin-details-skeleton-grid">
            {skeletonCards.map((_, idx) => (
              <div key={`detail-skeleton-${idx}`} className="admin-section skeleton-card">
                <div className="skeleton-line short" />
                <div className="skeleton-line" />
                <div className="skeleton-line" />
              </div>
            ))}
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <div className="admin-school-details-container">
        <div className="admin-error-message">{error}</div>
        <button onClick={() => navigate('/admin/entities')} className="admin-back-btn">
          Back to Entities
        </button>
      </div>
    );
  }

  if (!school) {
    return (
      <div className="admin-school-details-container">
        <div className="admin-error-message">School not found</div>
        <button onClick={() => navigate('/admin/entities')} className="admin-back-btn">
          Back to Entities
        </button>
      </div>
    );
  }

  return (
    <SidebarLayout>
        <div className="admin-school-details-container">
          <div className="admin-school-details-header">
            <div>
              <h1>{school.school_name}</h1>
              <div className="admin-school-details-meta">
                <span>ID: {school.entity_id}</span>
                <span>Created: {new Date(school.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <button onClick={() => navigate('/admin/entities')} className="admin-back-btn">
              Back to Entities
            </button>
            </div>
          <div className="admin-details-stats">
            <div className="admin-stat-card">
              <div className="stat-icon" aria-hidden="true">👥</div>
              <div>
                <span className="stat-label">Assigned Users</span>
                <span className="stat-value">{users.length}</span>
                <span className="stat-hint">Linked to this school</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" aria-hidden="true">📊</div>
              <div>
                <span className="stat-label">Data Schemas</span>
                <span className="stat-value">{schemas.length}</span>
                <span className="stat-hint">Structured tables available</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon" aria-hidden="true">🆔</div>
              <div>
                <span className="stat-label">Entity ID</span>
                <span className="stat-value smaller">{school.entity_id}</span>
                <span className="stat-hint">
                  Created {new Date(school.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          <div className="admin-school-details-content">
            <div className="admin-section">
              <div className="admin-section-header">
                <h2>Users ({users.length})</h2>
                <button onClick={() => setShowAddUser(!showAddUser)} className="admin-add-btn">
                  Add User
                </button>
              </div>

              {showAddUser && (
                <div className="admin-add-user-form">
                  <div className="admin-user-search-container">
                    <UserSearchDropdown
                      onUserSelect={handleUserSelect}
                      placeholder="Search for a user to add..."
                      excludeUserIds={users.map((user) => user.user_id)}
                    />
                  </div>
                  <div className="admin-add-user-actions">
                    <button
                      onClick={() => setShowAddUser(false)}
                      className="admin-cancel-btn"
                      disabled={addingUser}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {users.length === 0 ? (
                <div className="admin-empty-state">No users assigned to this school</div>
              ) : (
                <div className="admin-table admin-users-table">
                  <div className="admin-table-header">
                    <span>Name</span>
                    <span>Username</span>
                    <span>Email</span>
                    <span>Role</span>
                  </div>
                  <div className="admin-table-body">
                    {users.map((user) => (
                      <div key={user.user_id} className="admin-table-row">
                        <span className="cell-strong">{user.full_name || user.username}</span>
                        <span>@{user.username || 'unknown'}</span>
                        <span>{user.email || '—'}</span>
                        <span>{user.role || 'Member'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="admin-section">
              <div className="admin-section-header">
                <h2>Data Schemas ({schemas.length})</h2>
                <div className="admin-schema-actions">
                  {/* <button
                    onClick={() => navigate(`/admin/entities/${id}/tables`)}
                    className="admin-view-tables-btn"
                  >
                    View All Tables
                  </button> */}
                  <button
                    onClick={() => navigate(`/admin/entities/${id}/create-table`)}
                    className="admin-create-table-btn"
                  >
                    + Create New Table
                  </button>
                </div>
              </div>

              {schemas.length === 0 ? (
                <div className="admin-empty-state">No schemas defined for this school</div>
              ) : (
                <div className="admin-table admin-schemas-table">
                  <div className="admin-table-header">
                    <span>Table</span>
                    <span>Version</span>
                    <span>Created</span>
                    <span>Columns</span>
                    <span>Status</span>
                    <span className="actions-col">Actions</span>
                  </div>
                  <div className="admin-table-body">
                    {schemas.map((schema) => {
                      const columnCount = schema.schema_definition?.columns?.length ?? 0;
                      const isActive = schema.is_active !== false;
                      const isUpdating = updatingSchemaId === schema.schema_id;
                      return (
                        <div key={schema.schema_id} className="admin-table-row">
                          <span className="cell-strong">{schema.table_name}</span>
                          <span>{schema.version || '—'}</span>
                          <span>{new Date(schema.created_at).toLocaleDateString()}</span>
                          <span>{columnCount}</span>
                          <span>
                            <span className={`admin-schema-status ${isActive ? 'is-active' : 'is-inactive'}`}>
                              {isActive ? 'Active' : 'Inactive'}
                            </span>
                          </span>
                          <span className="actions-col table-actions">
                            <button
                              onClick={() => setAddSchemaId(schema.schema_id)}
                              className="admin-schema-action-btn admin-add-data-btn"
                              disabled={!isActive}
                            >
                              Add Data
                            </button>
                            <button
                              onClick={() => handleViewDataClick(schema.schema_id)}
                              className="admin-schema-action-btn admin-view-data-btn"
                            >
                              {viewSchemaId === schema.schema_id ? 'Hide Data' : 'View Data'}
                            </button>
                            <button
                              onClick={() => setEditSchemaId(schema.schema_id)}
                              className="admin-schema-action-btn admin-edit-table-btn"
                              disabled={!isActive}
                            >
                              Edit Table
                            </button>
                            <button
                              onClick={() => handleToggleSchemaActive(schema)}
                              className={`admin-schema-action-btn ${isActive ? 'admin-deactivate-table-btn' : 'admin-activate-table-btn'}`}
                              disabled={isUpdating}
                            >
                              {isUpdating
                                ? (isActive ? 'Deactivating...' : 'Activating...')
                                : (isActive ? 'Deactivate' : 'Activate')}
                            </button>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
          {viewSchemaId && (
            <div className="schema-view-panel" ref={viewPanelRef}>
              <div className="schema-panel-header">
                <div>
                  <p className="schema-panel-kicker">Viewing Data</p>
                  <h3>{schemaMap.get(viewSchemaId)?.table_name || 'Table Data'}</h3>
                  <p className="schema-panel-subtitle">
                    Manage records for this table without leaving the page.
                  </p>
                </div>
                <button type="button" className="schema-panel-close" onClick={() => setViewSchemaId(null)}>
                  ✕
                </button>
              </div>
              <ViewDataContent
                schemaId={viewSchemaId}
                isInline
                adminMode
                entityId={id}
                includeDeleted={viewIncludeDeleted}
                onToggleIncludeDeleted={() => setViewIncludeDeleted((prev) => !prev)}
                onAddData={() => setAddSchemaId(viewSchemaId)}
                refreshSignal={viewRefreshSignal}
              />
            </div>
          )}
        </div>
        {addSchemaId && (
          <div className="admin-schema-modal-overlay" onClick={() => setAddSchemaId(null)}>
            <div className="admin-schema-modal-card large" onClick={(e) => e.stopPropagation()}>
              <SchemaAddDataPanel
                entityId={id}
                schemaId={addSchemaId}
                onClose={() => setAddSchemaId(null)}
                onSuccess={handleAddDataSuccess}
              />
            </div>
          </div>
        )}
        <SchemaEditTableModal
          entityId={id}
          schemaId={editSchemaId}
          isOpen={!!editSchemaId}
          onClose={() => setEditSchemaId(null)}
          onSuccess={handleEditSchemaSuccess}
        />
    </SidebarLayout>
  );
};

export default AdminSchoolDetails;

